import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Calendar, LogOut, Gift, Trash2, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [mySelections, setMySelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wishlistDialogOpen, setWishlistDialogOpen] = useState(false);
  const [selectionsDialogOpen, setSelectionsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    event_name: '',
    event_date: '',
    event_time: ''
  });
  const [wishlistFormData, setWishlistFormData] = useState({
    name: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, wishlistsRes] = await Promise.all([
        axios.get(`${API}/events`, { withCredentials: true }),
        axios.get(`${API}/wishlists`, { withCredentials: true })
      ]);
      setEvents(eventsRes.data);
      
      // Fetch item counts for each wishlist
      const wishlistsWithCounts = await Promise.all(
        wishlistsRes.data.map(async (wishlist) => {
          try {
            const itemsRes = await axios.get(`${API}/wishlists/${wishlist.id}/items`, { withCredentials: true });
            return { ...wishlist, itemCount: itemsRes.data.length };
          } catch {
            return { ...wishlist, itemCount: 0 };
          }
        })
      );
      setWishlists(wishlistsWithCounts);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMySelections = async () => {
    try {
      const response = await axios.get(`${API}/my-selections`, { withCredentials: true });
      setMySelections(response.data);
      setSelectionsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching selections:', error);
      toast.error('Failed to load your selections');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    if (!formData.event_name || !formData.event_date || !formData.event_time) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API}/events`, {
        ...formData,
        create_new_wishlist: true
      }, { withCredentials: true });
      toast.success('Event created successfully!');
      setDialogOpen(false);
      setFormData({ event_name: '', event_date: '', event_time: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const handleCreateWishlist = async (e) => {
    e.preventDefault();
    
    if (!wishlistFormData.name) {
      toast.error('Please enter wishlist name');
      return;
    }

    try {
      await axios.post(`${API}/wishlists`, wishlistFormData, { withCredentials: true });
      toast.success('Wishlist created successfully!');
      setWishlistDialogOpen(false);
      setWishlistFormData({ name: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating wishlist:', error);
      toast.error('Failed to create wishlist');
    }
  };

  const handleDeleteEvent = async (eventId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success('Event deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleDeleteWishlist = async (wishlistId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this wishlist?')) {
      return;
    }

    try {
      await axios.delete(`${API}/wishlists/${wishlistId}`, { withCredentials: true });
      toast.success('Wishlist deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting wishlist:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete wishlist';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Centered Header with Logo */}
        <div className="text-center mb-6">
          <img 
            src={LOGO_URL}
            alt="My Wishlist Logo" 
            className="h-16 w-auto mx-auto cursor-pointer mb-4"
            onClick={() => navigate('/home')}
          />
          <div className="flex justify-end">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {user?.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
                <span className="text-sm font-medium text-gray-700">{user?.name}</span>
              </div>
              <Button onClick={logout} variant="outline" size="sm" className="gap-1">
                <LogOut className="w-3 h-3" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="glass-card p-6 mb-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-3">Welcome to Your Event Dashboard! 🎉</h2>
          <p className="text-gray-600 text-lg mb-2">
            Create unlimited events and wishlists for all your special occasions.
          </p>
          <p className="text-gray-600">
            Share them with loved ones and make every celebration memorable!
          </p>
        </div>

        {/* Quick Actions Bar */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-wrap gap-3 justify-center items-center">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600">
                  <Plus className="w-4 h-4" />
                  New Event
                </Button>
              </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New Event</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4">
                    <div>
                      <Label htmlFor="event_name">Event Name</Label>
                      <Input
                        id="event_name"
                        placeholder="e.g., Sarah's Birthday"
                        value={formData.event_name}
                        onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="event_date">Event Date</Label>
                      <Input
                        id="event_date"
                        type="date"
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="event_time">Event Time</Label>
                      <Input
                        id="event_time"
                        type="time"
                        value={formData.event_time}
                        onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full">Create Event</Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={wishlistDialogOpen} onOpenChange={setWishlistDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600">
                    <Plus className="w-4 h-4" />
                    New Wishlist
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New Wishlist</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateWishlist} className="space-y-4">
                    <div>
                      <Label htmlFor="wishlist_name">Wishlist Name</Label>
                      <Input
                        id="wishlist_name"
                        placeholder="e.g., Birthday Gifts 2025"
                        value={wishlistFormData.name}
                        onChange={(e) => setWishlistFormData({ name: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full">Create Wishlist</Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Button variant="outline" className="gap-2" onClick={fetchMySelections}>
                <Heart className="w-4 h-4" />
                Gifts I Gave
              </Button>
            </div>
          </div>
        </div>

        {/* Events Section */}
        <div className="mb-8">
          <div className="mb-4 pb-2 border-b-2 border-blue-400">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-blue-500" />
              My Events ({events.length})
            </h3>
          </div>
          {events.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No events yet. Create your first event!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="glass-card p-4 cursor-pointer hover:shadow-lg transition-all group relative"
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <Button
                    onClick={(e) => handleDeleteEvent(event.id, e)}
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{event.event_name}</h3>
                  <p className="text-sm text-gray-600 mb-1">{event.event_date} at {event.event_time}</p>
                  <p className="text-xs text-gray-500">By {event.creator_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Wishlists Section */}
        <div>
          <div className="mb-4 pb-2 border-b-2 border-purple-400">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Gift className="w-6 h-6 text-purple-500" />
              My Wishlists ({wishlists.length})
            </h3>
          </div>
          {wishlists.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Gift className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No wishlists yet. Create your first wishlist!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wishlists.map((wishlist) => (
                <div
                  key={wishlist.id}
                  className="glass-card p-4 cursor-pointer hover:shadow-lg transition-all group relative"
                  onClick={() => navigate(`/wishlists/${wishlist.id}`)}
                >
                  <Button
                    onClick={(e) => handleDeleteWishlist(wishlist.id, e)}
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{wishlist.name}</h3>
                  <p className="text-xs text-gray-500 mb-2">By {wishlist.creator_name}</p>
                  <div className="flex items-center gap-1 text-sm">
                    {wishlist.itemCount === 0 ? (
                      <span className="text-orange-500 font-medium">⚠️ Empty wishlist</span>
                    ) : (
                      <span className="text-gray-600">{wishlist.itemCount} {wishlist.itemCount === 1 ? 'item' : 'items'}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={selectionsDialogOpen} onOpenChange={setSelectionsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gifts I Gave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {mySelections.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">You haven&apos;t selected any gifts yet</p>
              </div>
            ) : (
              mySelections.map((selection) => (
                <div key={selection.item_id} className="border rounded-lg p-4">
                  <div className="flex gap-4">
                    <img 
                      src={selection.product_image_url} 
                      alt={selection.product_name}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">{selection.product_name}</h4>
                      <p className="text-sm text-gray-600">₹{selection.price}</p>
                      <p className="text-xs text-gray-500 mt-1">Event: {selection.event_name}</p>
                      <p className="text-xs text-green-600">Selected on {new Date(selection.selected_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dashboard;
